import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { UserPicker } from '../../../components/pickers/UserPicker';
import { type AuditQueryFilters } from '../types';

// Since date-fns is not in apps/web, I'll use native Date methods
function subtractDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getDifferenceInDays(d1: Date, d2: Date): number {
  return Math.abs(Math.round((d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24)));
}

interface AuditLogFilterBarProps {
  filters: AuditQueryFilters;
  onFiltersChange: (filters: AuditQueryFilters) => void;
  onExport: () => void;
  exportLoading?: boolean;
}

export function AuditLogFilterBar({
  filters,
  onFiltersChange,
  onExport,
  exportLoading,
}: AuditLogFilterBarProps) {
  const [localFrom, setLocalFrom] = useState(filters.from);
  const [localTo, setLocalTo] = useState(filters.to);
  const [error, setError] = useState<string | null>(null);

  const handleApply = () => {
    const fromDate = new Date(localFrom);
    const toDate = new Date(localTo);

    if (isAfter(fromDate, toDate)) {
      setError('From date must be before To date');
      return;
    }

    if (getDifferenceInDays(fromDate, toDate) > 90) {
      setError('Date range cannot exceed 90 days');
      return;
    }

    setError(null);
    onFiltersChange({ ...filters, from: localFrom, to: localTo, page: 1 });
  };

  return (
    <div className="bg-surface-1 border border-border rounded-[12px] p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField label="Event Type (e.g. auth.)">
          <Input
            value={filters.event_type || ''}
            onChange={(e) => onFiltersChange({ ...filters, event_type: e.target.value, page: 1 })}
            placeholder="Filter by type..."
          />
        </FormField>

        <FormField label="Actor / User">
          <UserPicker
            value={filters.actorId || ''}
            onChange={(val) => onFiltersChange({ ...filters, actorId: val, page: 1 })}
            placeholder="Filter by staff user..."
          />
        </FormField>

        <FormSelect
          label="Resource Type"
          value={filters.resource_type || ''}
          onChange={(e) => onFiltersChange({ ...filters, resource_type: e.target.value, page: 1 })}
          options={[
            { value: '', label: 'All Resources' },
            { value: 'chit_scheme', label: 'Chit Scheme' },
            { value: 'collection_receipt', label: 'Collection Receipt' },
            { value: 'user', label: 'User' },
          ]}
        />

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <FormField label="From Date">
              <Input
                type="date"
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                max={localTo}
              />
            </FormField>
          </div>
          <div className="flex-1">
            <FormField label="To Date">
              <Input
                type="date"
                value={localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                min={localFrom}
                max={formatDate(new Date())}
              />
            </FormField>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="text-xs text-danger-text">
          {error}
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" size="md" onClick={onExport} loading={exportLoading}>
            Export CSV
          </Button>
          <Button variant="primary" size="md" onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
}

// Helper to avoid isAfter if date-fns is not available
function isAfter(d1: Date, d2: Date) {
  return d1.getTime() > d2.getTime();
}
